import { useEffect, useId } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';

/**
 * Set of hook instance IDs that have requested fixed UI to be hidden.
 * Fixed UI (chat boxes, detail-page buttons, etc.) is hidden when this
 * set is non-empty.
 */
const fixedUIHiddenIdsAtom = atom<Set<string>>(new Set<string>());

const isFixedUIHiddenAtom = atom((get) => {
    return get(fixedUIHiddenIdsAtom).size > 0;
});

/**
 * Hook to temporarily hide fixed UI elements (chat boxes, detail-page
 * buttons, etc.). Fixed UI is hidden when at least one caller passes
 * `isHidden: true`.
 *
 * @returns Whether fixed UI is currently hidden.
 */
export function useFixedUIHidden({
    isHidden = false,
}: {
    isHidden?: boolean;
} = {}) {
    const hiddenRequestId = useId();
    const isFixedUIHidden = useAtomValue(isFixedUIHiddenAtom);
    const setHiddenRequestIds = useSetAtom(fixedUIHiddenIdsAtom);

    useEffect(() => {
        setHiddenRequestIds((prev) => {
            if (isHidden) {
                if (prev.has(hiddenRequestId)) {
                    return prev;
                }
                return new Set(prev).add(hiddenRequestId);
            }

            if (!prev.has(hiddenRequestId)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(hiddenRequestId);
            return next;
        });

        return () => {
            setHiddenRequestIds((prev) => {
                if (!prev.has(hiddenRequestId)) {
                    return prev;
                }
                const next = new Set(prev);
                next.delete(hiddenRequestId);
                return next;
            });
        };
    }, [isHidden, hiddenRequestId, setHiddenRequestIds]);

    return isFixedUIHidden;
}
